import type { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from "@reduxjs/toolkit/query"
import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes } from "types"
import NotificationEventTypeId from "types/enums/NotificationEventTypeId"

import { baseQueryWithAuth } from "utils/api"

type MonthlyEngagementReportQuery = {
  contractor_id: number
  reporting_period_from: string
  reporting_period_to: string
}

type MonthlyEngagementApiQuery = MonthlyEngagementReportQuery & {
  useMockData?: boolean
  page?: number
  per_page?: number
  outcome?: "all" | "answered" | "missed"
}

const monthlyEngagementReportEndpoints = {
  calls: "/contractors/engagement-reports/calls",
  call_outcomes: "/contractors/engagement-reports/call-outcomes",
  call_log: "/contractors/engagement-reports/call-log",
  engagement_summary: "/contractors/engagement-reports/engagement-summary",
  mobile_app_engagement: "/contractors/engagement-reports/mobile-app-engagement",
  partner_level_analytics: "/contractors/engagement-reports/partner-level-analytics",
  thermostat_engagement: "/contractors/engagement-reports/thermostat-engagement",
  event_types: "/notification/event-types",
} as const

type MonthlyEngagementBaseQuery = (args: {
  url: string
  method?: string
  params?: MonthlyEngagementApiQuery
  headers?: Record<string, string>
}) => Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta | undefined>>

type MonthlyEngagementQueryResult = QueryReturnValue<
  ApiTypes.Res.MonthlyEngagementReportResponse,
  FetchBaseQueryError,
  FetchBaseQueryMeta | undefined
>

type CustomerCallsAnalyticsResult = QueryReturnValue<
  { call_outcomes?: ApiTypes.Res.ICallOutcomesReport; call_log?: ApiTypes.Res.ICallLogReport },
  FetchBaseQueryError,
  FetchBaseQueryMeta | undefined
>

const getMonthlyEngagementResponseData = (response: unknown): unknown => {
  const responseData = response as {
    data?: unknown
  }

  if (responseData.data) {
    return responseData.data
  }

  return response
}

const normalizeEngagementSummaryResponse = (response: unknown): ApiTypes.Res.MonthlyEngagementSummary | undefined => {
  if (!response) return undefined

  const responseData = response as ApiTypes.Res.MonthlyEngagementReportResponse

  return responseData.engagement_summary ?? (response as ApiTypes.Res.MonthlyEngagementSummary)
}

const normalizeThermostatEngagementResponse = (
  response: unknown,
): ApiTypes.Res.MonthlyEngagementReportResponse["thermostat_engagement"] | undefined => {
  if (!response) return undefined

  const responseData = response as ApiTypes.Res.MonthlyEngagementReportResponse["thermostat_engagement"]

  if (responseData?.thermostat_engagement) {
    return responseData
  }

  return {
    thermostat_engagement: response as ApiTypes.Res.IThermostatEngagement,
  }
}

const normalizeMobileAppEngagementResponse = (
  response: unknown,
): ApiTypes.Res.MonthlyEngagementReportResponse["mobile_app_engagement"] | undefined => {
  if (!response) return undefined

  const responseData = response as ApiTypes.Res.MonthlyEngagementReportResponse["mobile_app_engagement"]

  if (responseData?.mobile_app_engagement) {
    return responseData
  }

  return {
    mobile_app_engagement: response as ApiTypes.Res.IMobileAppEngagement,
  }
}

const normalizeCallsResponse = (
  response: unknown,
): ApiTypes.Res.MonthlyEngagementReportResponse["calls"] | undefined => {
  if (!response) return undefined

  const responseData = response as ApiTypes.Res.ICallsEngagement & {
    calls?: ApiTypes.Res.ICallsEngagement
  }

  return responseData.calls ?? responseData
}

const normalizePartnerLevelAnalyticsResponse = (
  response: unknown,
): ApiTypes.Res.MonthlyEngagementReportResponse["partner_level_analytics"] | undefined => {
  if (!response) return undefined

  const responseData = response as ApiTypes.Res.MonthlyEngagementReportResponse["partner_level_analytics"]

  if (responseData?.partner_level_analytics) {
    return responseData
  }

  return {
    partner_level_analytics: response as ApiTypes.Res.IPartnerLevelAnalytics,
  }
}

const getEventTypesData = (response: unknown): ApiTypes.Model.EventType[] => {
  const responseData = getMonthlyEngagementResponseData(response) as
    | ApiTypes.Res.EventTypes
    | ApiTypes.Model.EventType[]
    | undefined

  if (Array.isArray(responseData)) {
    return responseData
  }

  return responseData?.data ?? []
}

export const fetchMonthlyEngagementSummary = async (
  monthlyEngagementQuery: MonthlyEngagementApiQuery,
  baseQuery: MonthlyEngagementBaseQuery,
): Promise<MonthlyEngagementQueryResult> => {
  const { useMockData, contractor_id, reporting_period_from, reporting_period_to } = monthlyEngagementQuery
  const monthlyEngagementQueryParams = { contractor_id, reporting_period_from, reporting_period_to }
  const eventTypesResult = await baseQuery({
    url: monthlyEngagementReportEndpoints.event_types,
    method: "GET",
  })

  if (eventTypesResult.error) {
    return { error: eventTypesResult.error }
  }

  const monthlyEngagementEventTypeExists = getEventTypesData(eventTypesResult.data).some(
    eventType => eventType.id === NotificationEventTypeId.MonthlyEngagementReport,
  )

  if (!monthlyEngagementEventTypeExists) {
    return {
      error: {
        status: 404,
        data: "Monthly engagement report event type is unavailable",
      },
    }
  }

  const mockHeaders = useMockData ? { "X-Nuve-Mock": "true" } : undefined

  const [summaryResult, mobileResult, callsResult, partnerResult, thermostatResult] = await Promise.all([
    baseQuery({
      url: monthlyEngagementReportEndpoints.engagement_summary,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(mockHeaders ? { headers: mockHeaders } : {}),
    }),
    baseQuery({
      url: monthlyEngagementReportEndpoints.mobile_app_engagement,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(mockHeaders ? { headers: mockHeaders } : {}),
    }),
    baseQuery({
      url: monthlyEngagementReportEndpoints.calls,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(mockHeaders ? { headers: mockHeaders } : {}),
    }),
    baseQuery({
      url: monthlyEngagementReportEndpoints.partner_level_analytics,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(mockHeaders ? { headers: mockHeaders } : {}),
    }),
    baseQuery({
      url: monthlyEngagementReportEndpoints.thermostat_engagement,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(mockHeaders ? { headers: mockHeaders } : {}),
    }),
  ])

  const firstError = [summaryResult, mobileResult, partnerResult, thermostatResult].find(result => result.error)?.error

  if (firstError) {
    return { error: firstError }
  }

  const engagementSummary = summaryResult.error
    ? undefined
    : normalizeEngagementSummaryResponse(getMonthlyEngagementResponseData(summaryResult.data))
  const mobileAppEngagement = mobileResult.error
    ? undefined
    : normalizeMobileAppEngagementResponse(getMonthlyEngagementResponseData(mobileResult.data))
  const partnerLevelAnalytics = partnerResult.error
    ? undefined
    : normalizePartnerLevelAnalyticsResponse(getMonthlyEngagementResponseData(partnerResult.data))
  const thermostatEngagement = thermostatResult.error
    ? undefined
    : normalizeThermostatEngagementResponse(getMonthlyEngagementResponseData(thermostatResult.data))
  // Calls data is optional; keep the rest of the report available if the Twilio-backed endpoint fails.
  const calls = callsResult.error
    ? undefined
    : normalizeCallsResponse(getMonthlyEngagementResponseData(callsResult.data))

  return {
    data: {
      engagement_summary: engagementSummary,
      calls,
      mobile_app_engagement: mobileAppEngagement,
      partner_level_analytics: partnerLevelAnalytics,
      thermostat_engagement: thermostatEngagement,
    },
  }
}

export const fetchCustomerCallsAnalytics = async (
  monthlyEngagementQuery: MonthlyEngagementApiQuery,
  baseQuery: MonthlyEngagementBaseQuery,
): Promise<CustomerCallsAnalyticsResult> => {
  const { useMockData, page, per_page, outcome, ...monthlyEngagementQueryParams } = monthlyEngagementQuery
  const headers = useMockData ? { "X-Nuve-Mock": "true" } : undefined
  const [outcomesResult, logResult] = await Promise.all([
    baseQuery({
      url: monthlyEngagementReportEndpoints.call_outcomes,
      method: "GET",
      params: monthlyEngagementQueryParams,
      ...(headers ? { headers } : {}),
    }),
    baseQuery({
      url: monthlyEngagementReportEndpoints.call_log,
      method: "GET",
      params: {
        ...monthlyEngagementQueryParams,
        ...(page ? { page } : {}),
        ...(per_page ? { per_page } : {}),
        ...(outcome ? { outcome } : {}),
      },
      ...(headers ? { headers } : {}),
    }),
  ])

  const firstError = [outcomesResult, logResult].find(result => result.error)?.error
  if (firstError) return { error: firstError }

  return {
    data: {
      call_outcomes: getMonthlyEngagementResponseData(outcomesResult.data) as ApiTypes.Res.ICallOutcomesReport,
      call_log: getMonthlyEngagementResponseData(logResult.data) as ApiTypes.Res.ICallLogReport,
    },
  }
}

export const engagementApi = createApi({
  reducerPath: "engagement",
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getMonthlyEngagementSummary: builder.query<ApiTypes.Res.MonthlyEngagementReportResponse, MonthlyEngagementApiQuery>(
      {
        queryFn: async (monthlyEngagementQueryParams, _queryApi, _extraOptions, baseQuery) =>
          fetchMonthlyEngagementSummary(monthlyEngagementQueryParams, baseQuery as MonthlyEngagementBaseQuery),
        keepUnusedDataFor: 0,
      },
    ),
    getCustomerCallsAnalytics: builder.query<
      { call_outcomes?: ApiTypes.Res.ICallOutcomesReport; call_log?: ApiTypes.Res.ICallLogReport },
      MonthlyEngagementApiQuery
    >({
      queryFn: async (monthlyEngagementQuery, _queryApi, _extraOptions, baseQuery) =>
        fetchCustomerCallsAnalytics(monthlyEngagementQuery, baseQuery as MonthlyEngagementBaseQuery),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useLazyGetMonthlyEngagementSummaryQuery,
  useGetMonthlyEngagementSummaryQuery,
  useLazyGetCustomerCallsAnalyticsQuery,
  useGetCustomerCallsAnalyticsQuery,
} = engagementApi
